import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RiCheckboxBlankCircleLine,
  RiCloseCircleFill,
  RiRecordCircleFill,
  RiRefreshLine,
} from 'react-icons/ri';
import { OrderingConstants, OrderingOperations } from 'polygon-ordering';
import getThemeLookup from '../selectors/getThemeLookup';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import Text from './Text';
import StandardButton from './StandardButton';
import TouchableOpacity from './TouchableOpacity';
import combineStyles from '../utils/combineStyles';
import EVENTS from '../constants/events';
import { logEvent } from '../utils/analytics';

const { PAYMENT_METHOD } = OrderingConstants;
const { removePaymentGatewayToken } = OrderingOperations;

function padCardDigits(last4: string, d = 'X'): string {
  const four = `${d}${d}${d}${d}`;
  return `${four}-${four}-${four}-${last4}`;
}

interface IProps {
  savedCard: PaymentGateWayToken;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  onRemoved: () => void;
}

const RedcatPaySavedCard: React.FC<IProps> = ({
  savedCard,
  selected,
  disabled,
  onSelect,
  onRemoved,
}) => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const dispatch = useAppDispatch();
  const [isRemovingToken, setIsRemovingToken] = useState(false);

  const IconComponent = selected ? RiRecordCircleFill : RiCheckboxBlankCircleLine;

  return (
    <TouchableOpacity
      style={styles.mainContainer}
      onClick={onSelect}
      disabled={disabled}
      disabledStyle={styles.disabled}
    >
      <div style={styles.rightContainer}>
        <div style={styles.topContainer}>
          <IconComponent style={combineStyles(p('paymentMethodIcon', ['color', 'fontSize']))} />

          <Text themeKey="paymentMethod" style={{ marginRight: 'auto', paddingLeft: 3 }}>
            {t(`paymentMethod.${PAYMENT_METHOD.SAVED_CARD}`)}
          </Text>
          <Text themeKey="savedCardNumber">{padCardDigits(savedCard.displayValue)}</Text>
        </div>

        <div style={combineStyles(p('paymentMethodSubtitle', ['color']), styles.removeRow)}>
          <StandardButton
            invisibleContainer
            label={isRemovingToken ? t('button.removingSavedCard') : t('button.removeSavedCard')}
            themeKey="removeSavedCardButton"
            LeftIconComponent={!isRemovingToken ? RiCloseCircleFill : null}
            iconStyle={p('removeSavedCardButtonIcon', ['color', 'fontSize'])}
            disabled={disabled}
            onClick={async () => {
              if (window.confirm(`Remove saved card ending in ${savedCard.displayValue}?`)) {
                setIsRemovingToken(true);
                await dispatch(removePaymentGatewayToken({ token: savedCard.token }))
                  .unwrap()
                  .then(() => logEvent(EVENTS.REMOVE_SAVED_CARD));
                onRemoved();
              }
              setIsRemovingToken(false);
            }}
          />
          {isRemovingToken && (
            <div style={styles.spinnerContainer}>
              <RiRefreshLine className="spinner" style={styles.loadingSpinner} />
            </div>
          )}
        </div>
      </div>
    </TouchableOpacity>
  );
};

export default RedcatPaySavedCard;

const styles: Styles = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  rightContainer: {
    flex: 1,
  },
  topContainer: {
    paddingTop: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  removeRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  spinnerContainer: {
    marginLeft: 4,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  loadingSpinner: {
    fontSize: 16,
  },
  disabled: {
    opacity: 0.5,
    filter: 'grayscale(100%)',
  },
};
